import React from "react";
import useAuth from "../hooks/useAuth.js";
import Navbar from "./Navbar.js";
import CallToAction from "./CallToAction.js";
import Footer from "./Footer.js";

function UserPolicy() {
  const { isAuthenticated } = useAuth();

  return (
    <section id="UserPolicy" className="bg-gray-50">
      <Navbar isAuthenticated={isAuthenticated} />

       <div className="container-fluid text-white  pl-16 py-5 shadow-sm page-header">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl font-extrabold tracking-tight">
              Ajo App User Policy
            </h1>
            <div className="mt-4 flex justify-center gap-4">
              <p className="text-lg text-gray-100">
                Effective: <span className="font-medium">29/11/2025</span>
              </p>
              <p className="text-lg text-gray-100">
                Last Updated: <span className="font-medium">26/09/2026</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <div className="bg-white shadow rounded-lg p-6 md:p-8 lg:p-10">
          {/* Section 1 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              1. User Responsibilities
            </h2>
            <p className="text-gray-700 mb-4">
              As a user of the Ajo App, you agree to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                <span className="font-medium">Provide Accurate Information:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Ensure all registration information (name, email, phone number, payment details) is accurate</li>
                </ul>
              </li>
              <li>
                <span className="font-medium">Timely Contributions:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Commit to making scheduled contributions in your thrift group</li>
                </ul>
              </li>
              <li>
                <span className="font-medium">Maintain Security:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Keep account credentials confidential</li>
                  <li>Report unauthorized access immediately</li>
                </ul>
              </li>
              <li>
                <span className="font-medium">Respect Other Users:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Engage respectfully with group members</li>
                  <li>Avoid abusive or fraudulent behavior</li>
                </ul>
              </li>
            </ul>
          </div>

          {/* Section 2 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              2. Acceptable Use
            </h2>
            <p className="text-gray-700 mb-4">
              To maintain a safe platform, users must not:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Use the platform for illegal or unauthorized purposes</li>
              <li>Provide false or misleading information</li>
              <li>Disrupt platform operations through hacking or spamming</li>
              <li>Harass, abuse, or defraud other users</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              3. Thrift Group Participation
            </h2>
            <p className="text-gray-700 mb-4">
              When joining or creating a thrift group:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>You agree to the group's terms (contributions, payout order, frequency)</li>
              <li>You must adhere to the group schedule</li>
              <li>
                <span className="font-medium">Missed contributions may result in:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Removal from the group</li>
                  <li>Loss of payout eligibility</li>
                  <li>Legal action in severe cases</li>
                </ul>
              </li>
            </ul>
          </div>

          {/* Section 4 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              4. Bank Account Linking and Payments
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Bank Account Linking:
                </h3>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Where a feature requires payment or payout details, users must provide and verify them with Ajo and/or the applicable payment provider.</li>
                  <li>Payment methods and payout setup may depend on the feature, transaction, supported currency, country, and provider eligibility.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Payments:
                </h3>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Ajo may use Stripe, Flutterwave, or other payment providers depending on the feature, transaction, currency, location, and provider availability. Ajo determines the available provider options.</li>
                  <li>A provider's confirmation of a payment is separate from any payout, transfer, or settlement review. Some transactions may require additional Ajo review or provider processing before funds are sent.</li>
                  <li>Any applicable fees will be presented before the user confirms a transaction.</li>
                  <li>Users must ensure sufficient funds and review the recipient, purpose, amount, and currency before confirming a payment.</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  KYC Verification:
                </h3>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Ajo and its payment providers may request identity, address, business, or payout verification as applicable. Verification may be required before payments or payouts are enabled.</li>
                </ul>
              </div>
            </div>
            <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-yellow-800 italic">
                <span className="font-semibold">Important:</span> Payment confirmation and payout, transfer, or settlement are separate steps and may be subject to review, verification, risk controls, and provider processing. A payment or payout under review is not necessarily refunded; any refund is a separate process subject to applicable law, provider rules, and the relevant feature's process. Provider terms and verification decisions also apply.
              </p>
            </div>
          </div>

          {/* Section 5 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              5. User Communication
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Notifications:
                </h3>
                <ul className="list-disc pl-6 space-y-1 text-gray-700">
                  <li>Reminders for contributions and group updates</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  Support:
                </h3>
                <p className="text-gray-700">
                  Contact support at{" "}
                  <a
                    href="mailto:support@ajoapp.ca"
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    support@ajoapp.ca
                  </a>
                </p>
              </div>
            </div>
          </div>

          {/* Section 6 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              6. Prohibited Activities
            </h2>
            <p className="text-gray-700 mb-4">
              Users are prohibited from:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>Creating fake accounts or impersonating others</li>
              <li>Using the app for money laundering or fraud</li>
              <li>Disrupting the platform or harming other users</li>
            </ul>
          </div>

          {/* Section 7 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              7. Consequences of Policy Violations
            </h2>
            <p className="text-gray-700 mb-4">
              Violations may result in:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-gray-700">
              <li>
                <span className="font-medium">Account Suspension or Termination:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Immediate suspension or permanent removal</li>
                </ul>
              </li>
              <li>
                <span className="font-medium">Forfeiture of Group Participation:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>Loss of contributions or payout eligibility</li>
                </ul>
              </li>
              <li>
                <span className="font-medium">Legal Action:</span>
                <ul className="list-disc pl-6 mt-1 space-y-1">
                  <li>In cases of fraud or severe breaches</li>
                </ul>
              </li>
            </ul>
          </div>

          {/* Section 8 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              8. Privacy and Data Protection
            </h2>
            <p className="text-gray-700 mb-4">
              User data is handled under our Privacy Policy and applicable data-protection laws. Read our{" "}
              <a
                href="/privacy"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Privacy Policy
              </a>{" "}
              before using Ajo's services.
            </p>
            <p className="text-gray-700">
              Financial and transaction data may be processed by Stripe, Flutterwave, or other providers depending on the Ajo service, feature, currency, location, and transaction.
            </p>
          </div>

          {/* Section 9 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              9. Dispute Resolution
            </h2>
            <p className="text-gray-700 mb-4">
              Disputes must be resolved amicably. Ajo App may mediate but is not liable for outcomes.
            </p>
            <p className="text-gray-700">
              Unresolved disputes may proceed to arbitration as outlined in our{" "}
              <a
                href="/termsandcondition"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                Terms and Conditions
              </a>.
            </p>
          </div>

          {/* Section 10 */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              10. Policy Updates
            </h2>
            <p className="text-gray-700">
              This policy may be updated periodically. Continued use of Ajo App indicates acceptance of updates.
            </p>
          </div>

          {/* Section 11 */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b pb-2">
              11. Contact Information
            </h2>
            <p className="text-gray-700 mb-4">
              For questions about this User Policy:
            </p>
            <p className="text-gray-700">
              Email:{" "}
              <a
                href="mailto:policy@ajoapp.ca"
                className="text-blue-600 hover:text-blue-800 font-medium"
              >
                policy@ajoapp.ca
              </a>
            </p>
            <p className="text-gray-700 mt-4 italic">
              By using Ajo App, you acknowledge reading and agreeing to this User Policy.
            </p>
          </div>
        </div>
      </div>

      <CallToAction />
      <Footer />
    </section>
  );
}

export default UserPolicy;